// The referral programme's data shapes and its one decision rule.
//
// Everything here is pure: no database, no clock, no randomness of its own. The
// caller passes in what it found and what the caller's facts are, and gets back
// a decision. That is what makes every refusal in `decideReferralClaim` testable
// without a CouchDB, and it is the reason this file exists separately from the
// server function that uses it.
//
// See design-docs/referral-program.md.
import { CouchUserInfo, SubscriptionStatus } from './couch-user';
import { PouchDoc } from './pouch';
import { REFERRAL_CODE_ALPHABET, REFERRAL_CODE_LENGTH, withoutCheckCharacter } from './code-check-character';

/** What a referral record's status says about the friend. */
export enum ReferralStatus {
  /** The friend created an account through an invite. The only status phase 1 ever writes. */
  SignedUp = 'signed_up',
  /** Reserved for phase 3, which decides when a referral has "stuck". Never written by phase 1. */
  Qualified = 'qualified',
}

/**
 * One per user, written the first time they open the referral hub.
 * Its id guarantees a person can only ever have one code.
 */
export interface ReferrerDoc extends PouchDoc {
  code: string;
  linkKey: string;
  /** A copy, refreshed whenever the hub is opened. See the design doc for why it may go stale. */
  inviterFirstName: string;
  createdAt_utc: string;
  /**
   * When this inviter was told their third friend had joined (#4963 phase 3).
   *
   * A record of what happened, not the guard that stops it happening twice — the
   * one-shot claim below is the guard, because two friends claiming in the same
   * instant can both read this field before either writes it. Absent means it has
   * not been sent.
   */
  thirdFriendNotifiedAt_utc?: string;
}

/**
 * One per code, written alongside the referrer document.
 * Its id is the code itself, so two people can never hold the same code.
 */
export interface ReferralCodeDoc extends PouchDoc {
  referrerUid: string;
  linkKey: string;
  createdAt_utc: string;
}

/**
 * One per referred friend.
 * Its id is the FRIEND's user id, which is what makes "one referrer per friend,
 * first link wins" true by construction rather than by a check that could be raced.
 */
export interface ReferralDoc extends PouchDoc {
  /** Empty string once the inviter deletes their account — the record survives, the identity does not. */
  referrerUid: string;
  code: string;
  status: ReferralStatus;
  signedUpAt_utc: string;
  /**
   * When this friend first completed a workout (#4963 phase 3).
   *
   * Absent until their OWN app reports it, and never overwritten afterwards. It is
   * what the dashboard's middle status is read from. Optional because every record
   * written before phase 3 shipped has none, and because most friends never train.
   */
  trainedAt_utc?: string;
  /**
   * When this record was created by a build whose every route in carries the
   * disclosure line (#4963 phase 3). Absent means it was created before phase 3
   * shipped, when no page said the inviter would see anything.
   *
   * Read only by `decideReferralFriendStatus`, which will not show Training without
   * it. Why it is a field rather than a date, and what it does and does not prove:
   * design-docs/referral-program.md, "The friends who joined before phase 3".
   */
  disclosedAt_utc?: string;
}

export const REFERRALS_DB = 'referrals';

export function referrerDocId(uid: string): string {
  return `referrer/${uid}`;
}

export function codeDocId(code: string): string {
  return `code/${code}`;
}

export function referralDocId(friendUid: string): string {
  return `referral/${friendUid}`;
}

/**
 * Which friend a referral record is about, read back out of its id.
 *
 * The record does not carry the friend's user id in a field of its own — its id IS
 * that id, which is what makes "one referrer per friend, first link wins" true by
 * construction. So a caller that needs the uid has to read it back, and this is the
 * one place allowed to: it derives the prefix from the builder above rather than
 * writing `'referral/'` out again. A caller slicing the string itself would go on
 * compiling and start answering nonsense the day the id scheme changed.
 *
 * Answers undefined for an id that is not a referral record's, so a caller cannot
 * mistake a mangled id for a real user.
 */
export function friendUidFromReferralDocId(docId: string): string | undefined {
  const prefix = referralDocId('');
  if (!docId.startsWith(prefix)) return undefined;
  return docId.slice(prefix.length) || undefined;
}

/**
 * One per checkout we offered a referral price for (#4962 phase 2).
 *
 * It is its OWN document rather than an entry on `referral/<friendUid>` because the
 * checkout path runs on EVERY visit to the subscribe page: an appended list would
 * grow without bound on one document and would conflict every time two tabs loaded
 * at once. A document per checkout id has neither problem, because the id is unique
 * by construction, so two writers never touch one document.
 *
 * It is about ONE person. The friend's own user id is the only identifier in it —
 * no name, no email, and nothing whatever from the inviter.
 */
export interface ReferralOfferDoc extends PouchDoc {
  /** The person the offer was made to. Also in the id — see `referralOfferDocId`. */
  friendUid: string;
  /** The checkout this offer was made for. Minted inside the Paddle checkout builder. */
  checkoutId: string;
  /** The referral price ids actually handed out, so what was offered is recorded rather than re-derived. */
  offeredPriceIds: string[];
  createdAt_utc: string;
  /**
   * When this offer stops counting. 24 hours: comfortably longer than any real
   * checkout, and far shorter than the months over which a stale offer would
   * otherwise excuse a purchase that was never entitled to one.
   */
  expiresAt_utc: string;
}

/** How long an offer record counts for. See `ReferralOfferDoc.expiresAt_utc`. */
export const REFERRAL_OFFER_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * The id of one offer document.
 *
 * The friend's uid is in the id as well as the checkout id, and both readers already
 * have it — the checkout path is writing for a caller it has just resolved, and the
 * webhook resolves the subscriber's uid before it reaches this check. What it buys is
 * that every offer document belonging to one person shares a common prefix, so a plain
 * `allDocs` range read over `referralOfferDocIdPrefix(uid)` finds all of them with no
 * new CouchDB view. That one read is what makes the expiry sweep, the account-deletion
 * clean-up and the data export possible.
 */
export function referralOfferDocId(friendUid: string, checkoutId: string): string {
  return `referral_offer/${friendUid}/${checkoutId}`;
}

/**
 * The id prefix covering every offer document belonging to one person.
 *
 * Kept beside the id builder deliberately. The export in `tools/` once wrote an id
 * scheme out as a literal instead of importing it; the copy kept matching itself after
 * the scheme changed, and the export silently produced an empty referral section.
 */
export function referralOfferDocIdPrefix(friendUid: string): string {
  return `referral_offer/${friendUid}/`;
}

/** Whether an offer document has passed its expiry, as at `nowMs`. */
export function isReferralOfferExpired(offer: Pick<ReferralOfferDoc, 'expiresAt_utc'>, nowMs: number): boolean {
  const expiresMs = Date.parse(offer.expiresAt_utc);
  // An unparseable expiry counts as EXPIRED. The two things that read this are the
  // sweep, which would then delete it, and the webhook's match, which would then
  // report the purchase — and a record we cannot read the expiry of is one we cannot
  // vouch for, so refusing to honour it is the direction that cannot excuse a
  // purchase nobody offered.
  if (Number.isNaN(expiresMs)) return true;
  return expiresMs <= nowMs;
}

// The alphabet and length live in code-check-character.ts (#6984), beside the check
// character computed over them; re-exported here for the callers that import them from this file.
export { REFERRAL_CODE_ALPHABET, REFERRAL_CODE_LENGTH };

/** How many bytes of randomness the unguessable half of an invite link carries. */
export const REFERRAL_LINK_KEY_BYTES = 16;

/**
 * The separator between the readable code and the unguessable link key.
 * It must not appear in either half — the code alphabet excludes it, and
 * base64url uses only letters, digits, `-` and `_`.
 */
const REF_VALUE_SEPARATOR = '.';

/**
 * A source of random bytes.
 *
 * It is injected rather than imported because this package has no runtime crypto
 * of its own: it compiles for the browser, the server and the command-line tools,
 * and those three do not agree on how to get random bytes. Injecting it also lets
 * the tests supply a fixed sequence and assert on the exact output.
 */
export type RandomBytesSource = (byteCount: number) => Uint8Array;

/** Builds a readable referral code from random bytes. */
export function generateReferralCode(randomBytes: RandomBytesSource): string {
  const bytes = randomBytes(REFERRAL_CODE_LENGTH);
  if (bytes.length < REFERRAL_CODE_LENGTH) {
    throw new Error(`generateReferralCode: needed ${REFERRAL_CODE_LENGTH} random bytes, got ${bytes.length}`);
  }
  let code = '';
  // take the low 5 bits of each byte, which indexes the 32-character alphabet exactly
  for (let i = 0; i < REFERRAL_CODE_LENGTH; i++) {
    code += REFERRAL_CODE_ALPHABET[bytes[i] & 0b11111];
  }
  return code;
}

/** Builds the unguessable half of an invite link: 128 random bits as base64url. */
export function generateLinkKey(randomBytes: RandomBytesSource): string {
  const bytes = randomBytes(REFERRAL_LINK_KEY_BYTES);
  if (bytes.length < REFERRAL_LINK_KEY_BYTES) {
    throw new Error(`generateLinkKey: needed ${REFERRAL_LINK_KEY_BYTES} random bytes, got ${bytes.length}`);
  }
  return toBase64Url(bytes.subarray(0, REFERRAL_LINK_KEY_BYTES));
}

const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

// base64url, written out here because this package cannot reach for Buffer (not in
// the browser) or btoa (not in Node's ES2022 lib types). Padding is omitted, which
// is what makes 16 bytes come out as 22 characters.
function toBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : undefined;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : undefined;

    out += BASE64URL_ALPHABET[b0 >> 2];
    out += BASE64URL_ALPHABET[((b0 & 0b11) << 4) | ((b1 ?? 0) >> 4)];
    if (b1 === undefined) break;
    out += BASE64URL_ALPHABET[((b1 & 0b1111) << 2) | ((b2 ?? 0) >> 6)];
    if (b2 === undefined) break;
    out += BASE64URL_ALPHABET[b2 & 0b111111];
  }
  return out;
}

/** The `ref` value that goes in an invite link: the readable code, then the link key. */
export function buildRefValue(code: string, linkKey: string): string {
  return `${code}${REF_VALUE_SEPARATOR}${linkKey}`;
}

/**
 * Builds a whole invite link.
 *
 * `origin` is passed in rather than built here because the two callers get it from
 * different places — `getAppDomain()` on the server, `getSharedUrlOrigin()` on the
 * client — and neither is reachable from this package. Hardcoding the production
 * domain would make the link unfollowable while developing and in the end-to-end
 * test, which run against a local origin.
 */
export function buildInviteLink(origin: string, code: string, linkKey: string): string {
  const trimmedOrigin = origin.replace(/\/+$/, '');
  return `${trimmedOrigin}/invite?ref=${encodeURIComponent(buildRefValue(code, linkKey))}`;
}

/** What a `ref` value turned out to contain. A bare code has no link key. */
export interface ParsedRefValue {
  code: string;
  /** Absent when the user typed a bare code rather than following a link. */
  linkKey?: string;
}

/**
 * Reads a `ref` value.
 *
 * Returns undefined when there is nothing usable in it. Anything with more than one
 * separator is rejected rather than guessed at, because a value we cannot read
 * exactly is one we should refuse rather than partially believe.
 */
export function parseRefValue(raw: unknown): ParsedRefValue | undefined {
  // Typed as `unknown` rather than `string` on purpose. Both callers hand this
  // whatever arrived in the request body, and nothing validates that JSON — so a
  // caller sending `{"ref": 123}` or `{"ref": ["x"]}` reaches here with a value
  // that is truthy but has no `.trim`. Calling it would throw, and on the PUBLIC
  // referralPeekInvite that turns into a 500 while every other failure answers a
  // plain 200 `{valid:false}` — which breaks the rule that all its failures must
  // be indistinguishable, in status as well as body.
  if (typeof raw !== 'string') return undefined;
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const parts = trimmed.split(REF_VALUE_SEPARATOR);
  if (parts.length > 2) return undefined;

  const [shownCode, linkKey] = parts;
  // a bare typed code may carry the check character the app shows (#6984); a link never does
  const code = parts.length === 1 ? (withoutFriendCheck(shownCode) ?? shownCode) : shownCode;
  if (!isPlausibleCode(code)) return undefined;
  if (parts.length === 1) return { code };
  if (!linkKey) return undefined;
  return { code, linkKey };
}

/**
 * The stored 8-character code inside a 9-character friend code whose last character
 * is its correct check character (#6984), else undefined. Undefined covers both a
 * code of another length and a mistyped one, so callers fall back to the text as typed.
 */
export function withoutFriendCheck(shown: string): string | undefined {
  const body = withoutCheckCharacter(shown, REFERRAL_CODE_ALPHABET, REFERRAL_CODE_LENGTH);
  return body && isPlausibleCode(body) ? body : undefined;
}

/**
 * Tidies up a code a PERSON typed, before anything tries to use it.
 *
 * The alphabet is upper-case only, so a code typed as `k7mq…` fails the shape
 * check below and the person is told we do not recognise it — which is both
 * wrong and unhelpful, since a phone keyboard produces lower case after the
 * first character, and the box that accepts a typed code exists precisely for
 * somebody whose invite link already failed. Spaces are dropped for the same
 * reason: a code read out loud gets written down in groups.
 *
 * **The case change is skipped for a whole `<CODE>.<LINKKEY>` value.** The link
 * key is 22 base64url characters, where upper and lower case are different
 * characters, so upper-casing one would turn a valid invite into an invalid one
 * and the failure would look exactly like a wrong link. Somebody pasting a whole
 * invite value into the code box is unlikely — the app only ever shows them the
 * 8-character code — but it costs one check to keep working, and this function is
 * the only place that knows what the separator is.
 */
export function normalizeTypedReferralCode(raw: string): string {
  const withoutSpaces = raw.replace(/\s+/g, '');
  // if it carries a link key, only the spaces come out: the key is case-sensitive
  if (withoutSpaces.includes(REF_VALUE_SEPARATOR)) return withoutSpaces;
  return withoutSpaces.toUpperCase();
}

/**
 * Whether a string could be one of our codes at all.
 *
 * This is a shape check, never a claim that the code exists — only the database can
 * say that. It exists so a value that could not possibly be a code is rejected
 * before it costs a read.
 */
export function isPlausibleCode(code: string | undefined): boolean {
  if (!code || code.length !== REFERRAL_CODE_LENGTH) return false;
  for (const ch of code) {
    if (!REFERRAL_CODE_ALPHABET.includes(ch)) return false;
  }
  return true;
}

/**
 * How long after an account is created it may still attach itself to an invite.
 *
 * The offer is for new accounts, and this is what stops an account created a year
 * ago being attached to an invite today. Seven days is a judgement, not a measured
 * figure: it comfortably covers somebody who follows a link, creates an account,
 * and takes a few days to come back to the app. Change it if that turns out to be
 * wrong — nothing else depends on the exact number.
 */
export const REFERRAL_MAX_ACCOUNT_AGE_DAYS = 7;

/**
 * Whether this account has a subscription of its own, for the purposes of the
 * "new accounts only" rule.
 *
 * It inspects the four PAYMENT rails only — Apple, Google Play, Paddle and PayPro.
 * The coach blocks are deliberately not counted, and that is the whole point of
 * this function existing rather than the check being written inline: a client whose
 * COACH pays for them has no subscription of their own, and is exactly the kind of
 * person a friend might invite. Counting `coachData` here would refuse them.
 *
 * Any status other than `none` counts, including a lapsed or cancelled one. The
 * offer is for accounts that have never been a customer, not for accounts that are
 * not a customer today.
 */
export function hasBlockingPaymentSubscription(user: Pick<CouchUserInfo, 'payProData' | 'paddleData' | 'googlePlayData' | 'appleData'> | null | undefined): boolean {
  if (!user) return false;
  const blocks = [user.payProData, user.paddleData, user.googlePlayData, user.appleData];
  // A block with NO status at all counts as blocking, and that is a deliberate
  // choice rather than a side effect of the comparison. Such blocks are really
  // written — appleServerNotification stores `{ originalTransactionId }` and the
  // Google Play notification handler stores `{ purchaseToken }` before any status
  // is known — so the account has begun a purchase even though nothing says so
  // yet. Refusing is the safe direction: the cost is that one person is told the
  // offer is for new accounts, and they can ask support; the cost of the other
  // direction is giving a paying customer a new-account offer.
  return blocks.some((block) => !!block && block.subscriptionStatus !== SubscriptionStatus.None);
}

/**
 * Whether this account has ever used a free trial, on any of the four PAYMENT rails
 * (#4962 phase 2). Part of the "no stacking" guardrail: somebody who has already had
 * a trial does not get a second, longer one.
 *
 * Written out here rather than reusing anything, because there is nothing to reuse
 * and the near-miss is easy to reach for. `hasBlockingPaymentSubscription` above
 * answers the DIFFERENT question one row up in the entitlement table — it compares
 * `subscriptionStatus` and never reads `trialUsed` at all. The only existing
 * `trialUsed` reader is `hasPayProHistory` in `select-web-provider.ts`, which is
 * PayPro-only.
 *
 * The two COACH blocks are excluded, matching `hasBlockingPaymentSubscription` and
 * for the same reason: a coach seat is somebody else paying, and it says nothing
 * about whether this person has ever had a trial of their own.
 *
 * A recorded PayPro trial end date also counts (#6552), which is the rule the PayPro
 * checkout itself has always applied before adding `skip-trial-period`. Older PayPro
 * accounts carry the date without the flag. The Paddle checkout asks this same
 * question to decide whether a returning customer gets the trial again.
 */
export function hasUsedTrialOnAnyRail(
  user: Pick<CouchUserInfo, 'payProData' | 'paddleData' | 'googlePlayData' | 'appleData'> | null | undefined,
): boolean {
  if (!user) return false;
  const blocks = [user.payProData, user.paddleData, user.googlePlayData, user.appleData];
  if (blocks.some((block) => block?.trialUsed === true)) return true;
  const payProTrialEnd = user.payProData?.trialEnds_utc;
  return typeof payProTrialEnd === 'string' && payProTrialEnd.length > 0;
}

/** Why a referred friend is not being offered the longer trial. One value per row of the entitlement table. */
export enum ReferralTrialRefusal {
  /**
   * The programme was switched off for the person who sent the invite. No longer
   * produced since the config switch was removed (#6769); kept so a value already
   * logged under it still has a name.
   */
  FlagOffForInviter = 'flag-off-for-inviter',
  /** The referral record has no inviter left — they deleted their account. */
  NoInviter = 'no-inviter',
  /** No referral record for this caller: they were never referred. */
  NotReferred = 'not-referred',
  /** The referral has already moved past sign-up. */
  AlreadyConverted = 'already-converted',
  /** The caller has, or has had, a subscription of their own. */
  HasSubscription = 'has-subscription',
  /** The caller has already used a trial somewhere. No stacking. */
  TrialAlreadyUsed = 'trial-already-used',
}

/** What the caller needs to know about the person asking for a checkout. */
export interface ReferralTrialFacts {
  /**
   * The caller's referral record, or undefined when they have none.
   * `referral/<callerUid>` — so it is theirs by construction, never looked up by code.
   */
  referral: Pick<ReferralDoc, 'referrerUid' | 'status'> | undefined;
  /** The caller's own subscription blocks. */
  user: Pick<CouchUserInfo, 'payProData' | 'paddleData' | 'googlePlayData' | 'appleData'> | null | undefined;
}

/** Entitled, or the single reason why not. */
export type ReferralTrialDecision =
  | { entitled: true }
  | { entitled: false; refusal: ReferralTrialRefusal };

/**
 * Whether this caller gets the longer referral trial (#4962 phase 2).
 *
 * Pure: no database, no clock, no network. The caller reads the referral record;
 * this decides. Same shape and same reasoning as
 * `decideReferralClaim` below.
 *
 * The rows are checked in the order of the entitlement table in
 * design-docs/referral-program.md, and every one of them is a refusal that a reader
 * can act on rather than a bare false.
 */
export function decideReferralTrialEntitlement(facts: ReferralTrialFacts): ReferralTrialDecision {
  const { referral } = facts;
  if (!referral) return { entitled: false, refusal: ReferralTrialRefusal.NotReferred };

  // An EMPTY inviter uid names nobody, so it is refused. Phase 1 clears this field to
  // an empty string when the inviter deletes their account: the record survives, the
  // identity does not, and an orphaned referral is not owed a free month.
  if (!referral.referrerUid) return { entitled: false, refusal: ReferralTrialRefusal.NoInviter };

  // Anything past sign-up has already converted. `Qualified` is declared but never
  // written until phase 3, so this arm is defensive until then — it is written now so
  // that phase 3 cannot turn it on and silently hand a second offer to somebody.
  if (referral.status !== ReferralStatus.SignedUp) {
    return { entitled: false, refusal: ReferralTrialRefusal.AlreadyConverted };
  }

  if (hasBlockingPaymentSubscription(facts.user)) {
    return { entitled: false, refusal: ReferralTrialRefusal.HasSubscription };
  }
  if (hasUsedTrialOnAnyRail(facts.user)) {
    return { entitled: false, refusal: ReferralTrialRefusal.TrialAlreadyUsed };
  }
  return { entitled: true };
}

/**
 * Whether to offer the "were you invited?" code-entry box.
 *
 * Only somebody who has no referrer yet can gain anything from typing a code, and
 * showing it to a person who already has one invites them to try a second code that
 * will certainly be refused.
 *
 * Lives HERE rather than in `client/src/services/referral.ts`, where it started,
 * because phase 2 puts the same box on the subscribe page and the SERVER decides
 * whether to show it there — and `server/functions/src` cannot import client code.
 * Restating the rule server-side instead would reintroduce exactly the drift that
 * extracting it prevented.
 */
export function shouldOfferCodeEntry(hasReferrer: boolean): boolean {
  return !hasReferrer;
}

/** What the server knows when deciding the subscribe page's code box (#6985). */
export interface SubscribeCodeEntryFacts {
  /** The caller already has a referral record. */
  hasReferrer: boolean;
  /** The caller's own subscription blocks. */
  user: Pick<CouchUserInfo, 'payProData' | 'paddleData' | 'googlePlayData' | 'appleData'> | null | undefined;
  /** When the caller's account was created; `null` when the lookup failed. */
  accountCreatedAt_utc: string | null;
  now_utc: string;
}

/**
 * Whether the SUBSCRIBE page may offer the "were you invited?" box (#6985).
 *
 * Narrower than `shouldOfferCodeEntry`, which the referral hub still uses. The
 * subscribe page put the box above the plans for every signed-in web user with no
 * referrer, so a person who came to redeem an access code typed it there and was
 * refused. It is now offered only to an account that could still be granted the free
 * month: the claim's own subscription and account-age rules, plus the entitlement's
 * trial rule. The other half of the decision — did this browser come from an invite
 * link — only the client knows, so the subscribe page applies it.
 */
export function shouldOfferSubscribeCodeEntry(facts: SubscribeCodeEntryFacts): boolean {
  return shouldOfferCodeEntry(facts.hasReferrer)
    && !hasBlockingPaymentSubscription(facts.user)
    && !hasUsedTrialOnAnyRail(facts.user)
    && isAccountNewEnoughToClaim(facts.accountCreatedAt_utc, facts.now_utc);
}

/** Why a claim was refused. Each value is one row of the refusal table in the design doc. */
export enum ReferralClaimRefusal {
  /** The value did not resolve: no such code, or a link key that did not match. The two are deliberately the same answer. */
  UnknownInvite = 'unknown-invite',
  /** Nobody may refer themselves. */
  SelfReferral = 'self-referral',
  /** This friend already has a referrer, and it is somebody else. First link wins. */
  AlreadyReferredByAnother = 'already-referred-by-another',
  /** The caller has, or has had, a subscription of their own. The offer is for new accounts. */
  HasSubscription = 'has-subscription',
  /** The caller's account is older than the offer allows — or we could not find out how old it is. */
  AccountTooOld = 'account-too-old',
  /** The inviter's account is gone, so there is nobody to attach this friend to. */
  InviterGone = 'inviter-gone',
}

/** Everything `decideReferralClaim` needs to know. Gathered by the caller; never fetched here. */
export interface ReferralClaimFacts {
  /** The user making the claim. */
  callerUid: string;
  /** What the `ref` value parsed to, or undefined when it could not be read. */
  parsedRef: ParsedRefValue | undefined;
  /** The `code/<CODE>` document, or null when there is no such code. */
  codeDoc: ReferralCodeDoc | null;
  /** Whether the inviter's `referrer/<uid>` document is still there. */
  inviterReferrerExists: boolean;
  /** This caller's existing `referral/<uid>` document, or null when they have none. */
  existingReferral: ReferralDoc | null;
  /** Whether the caller has a block on ANY of the four payment rails whose status is not `none`. Coach blocks are deliberately not counted. */
  callerHasPaymentSubscription: boolean;
  /**
   * When the caller's account was created, from the Firebase authentication record.
   * `null` means the lookup FAILED, which is refused rather than allowed — see the
   * design doc. It never means "no account".
   */
  callerAccountCreatedAt_utc: string | null;
  /** Now, supplied by the caller so this function has no clock of its own. */
  now_utc: string;
}

/** What should happen. Exactly one of: write the record, do nothing, or refuse. */
export type ReferralClaimDecision =
  | { kind: 'write'; record: ReferralDoc }
  /** The caller already has a record naming this same inviter. The call succeeds and writes nothing. */
  | { kind: 'already-claimed'; referrerUid: string }
  | { kind: 'refuse'; refusal: ReferralClaimRefusal };

/**
 * Decides whether a friend may be attached to an inviter.
 *
 * The order of the checks is load-bearing in one place: the already-claimed check
 * sits ABOVE the subscription and account-age checks. That is what makes a repeat
 * call succeed. Without it, somebody who claimed an invite on day one and called
 * again after subscribing would be refused for a record they already hold, which
 * would look to them like the app forgetting their referral.
 */
export function decideReferralClaim(facts: ReferralClaimFacts): ReferralClaimDecision {
  const refuse = (refusal: ReferralClaimRefusal): ReferralClaimDecision => ({ kind: 'refuse', refusal });

  // 1. the value must resolve to a code we hold, and — when a link key was supplied —
  //    it must match. A wrong link key and an unknown code give the SAME answer, so
  //    that this can never become a way to find out which codes exist.
  const parsed = facts.parsedRef;
  if (!parsed || !facts.codeDoc) return refuse(ReferralClaimRefusal.UnknownInvite);
  if (parsed.linkKey !== undefined && parsed.linkKey !== facts.codeDoc.linkKey) {
    return refuse(ReferralClaimRefusal.UnknownInvite);
  }

  const referrerUid = facts.codeDoc.referrerUid;

  // 2. nobody may refer themselves
  if (referrerUid === facts.callerUid) return refuse(ReferralClaimRefusal.SelfReferral);

  // 3. first link wins. the same inviter again is not a refusal — it is the repeat
  //    call, which succeeds and writes nothing.
  if (facts.existingReferral) {
    if (facts.existingReferral.referrerUid === referrerUid) {
      return { kind: 'already-claimed', referrerUid };
    }
    return refuse(ReferralClaimRefusal.AlreadyReferredByAnother);
  }

  // 4. the offer is for new accounts, so somebody already paying is out
  if (facts.callerHasPaymentSubscription) return refuse(ReferralClaimRefusal.HasSubscription);

  // 5. and so is an old account. a lookup we could not make is refused rather than
  //    waved through: this rule exists to stop an old account attaching itself to an
  //    invite, so failing open would defeat it, and a refused claim can be retried.
  if (!isAccountNewEnoughToClaim(facts.callerAccountCreatedAt_utc, facts.now_utc)) {
    return refuse(ReferralClaimRefusal.AccountTooOld);
  }

  // 6. the inviter must still exist
  if (!facts.inviterReferrerExists) return refuse(ReferralClaimRefusal.InviterGone);

  return {
    kind: 'write',
    record: {
      _id: referralDocId(facts.callerUid),
      referrerUid,
      // taken from the parsed value, not by stripping the document id: codeDocId()
      // is the one owner of that prefix, and re-deriving it here would silently
      // start storing `code/ABCDEFGH` the day that builder changed
      code: parsed.code,
      status: ReferralStatus.SignedUp,
      signedUpAt_utc: facts.now_utc,
      // the same moment, deliberately stored as its own field rather than inferred
      // from the one above: what this records is that the code creating the record
      // shows the disclosure line, and a later change to when a record is stamped
      // must not be silently answered by the sign-up time
      disclosedAt_utc: facts.now_utc,
    },
  };
}

/**
 * Whether an account is new enough to attach itself to an invite.
 *
 * Every answer it cannot work out is `false`, which refuses the claim. A missing
 * creation time means the lookup failed, and an unreadable date means the same in
 * practice — in both cases we do not know how old the account is, and the whole
 * point of the rule is to stop an old one slipping through.
 */
export function isAccountNewEnoughToClaim(
  accountCreatedAt_utc: string | null | undefined,
  now_utc: string,
): boolean {
  if (!accountCreatedAt_utc) return false;
  const created = Date.parse(accountCreatedAt_utc);
  const now = Date.parse(now_utc);
  if (Number.isNaN(created) || Number.isNaN(now)) return false;

  const ageDays = (now - created) / (1000 * 60 * 60 * 24);
  // a clock skew that puts creation slightly in the future is not an old account,
  // so a negative age passes rather than being treated as unreadable
  return ageDays <= REFERRAL_MAX_ACCOUNT_AGE_DAYS;
}

// ---------------------------------------------------------------------------
// Qualification (#4963 phase 3) — the friend has actually paid.
// ---------------------------------------------------------------------------

/** What a paid charge means for the friend's referral record. */
export enum ReferralQualificationAction {
  /** This person has no referral record. Nobody invited them, so there is nothing to do. */
  NoReferral = 'no-referral',
  /** The record says `signed_up`. Move it to `qualified`. */
  Promote = 'promote',
  /**
   * The record already says `qualified`.
   *
   * Nothing needs writing — but the caller must still go on and try the
   * notification claim, because this is exactly what a crash between the status
   * write and the notification leaves behind. Treating it as "already handled"
   * would strand that inviter, permanently and silently.
   */
  AlreadyQualified = 'already-qualified',
}

/**
 * Decide what a genuine paid charge by `friendUid` means for their referral record.
 *
 * Pure, so every case is testable with no database: the caller reads the record and
 * hands it in. It deliberately knows nothing about the feature flag — the status
 * change is NEVER gated on it, because a charge landing while the flag is off would
 * otherwise be lost until a renewal a month later, and on a yearly plan a year later.
 * Only the notification and the dashboard are gated.
 *
 * The status only ever rises. There is no rule here that lowers it, and that is
 * deliberate rather than an omission: `qualified` means "has paid at least once",
 * not "is paying now". A friend who pays and then refunds a week later stays
 * `qualified`, because lowering it would tell the inviter something about somebody
 * else's refund — further into that person's business than this feature should ever
 * go — and the inviter has already been notified anyway.
 */
export function decideReferralQualification(
  referral: Pick<ReferralDoc, 'status'> | null | undefined,
): ReferralQualificationAction {
  if (!referral) return ReferralQualificationAction.NoReferral;
  return referral.status === ReferralStatus.Qualified
    ? ReferralQualificationAction.AlreadyQualified
    : ReferralQualificationAction.Promote;
}

/**
 * The claim key that makes "tell the inviter once" true for ever.
 *
 * Keyed on the FRIEND, so one qualification per friend for the rest of time. It is
 * deliberately NOT keyed per subscription the way the Paddle conversion pixels are:
 * somebody who cancels and resubscribes would then be announced a second time.
 *
 * This exists because the paid-charge condition it hangs off is reached on EVERY
 * renewal, for every subscriber, for ever — so without it the inviter is told
 * "they became a member" once a month, indefinitely. A redelivered webhook is the
 * smaller version of the same problem.
 */
export function referralQualifiedClaimKey(friendUid: string): string {
  return `referral_qualified_notify:${friendUid}`;
}

// ---------------------------------------------------------------------------
// Training (#4963 phase 3) — the friend has actually started training.
// ---------------------------------------------------------------------------

/**
 * Why a training report was not recorded. Each value is a case the pure rule
 * below can answer with; only one of them can ever reach a signed-in caller.
 */
export enum ReferralTrainingRefusal {
  /**
   * This person has no referral record. Nobody invited them, so there is nothing
   * to flag.
   *
   * **This is a NORMAL outcome, not a fault.** The client only calls when it holds
   * a local "I was referred" marker, and that marker and the server's records can
   * disagree — a device restore is the ordinary way it happens. Answer it plainly
   * and do not file it as a problem.
   */
  NoReferral = 'no-referral',
  /**
   * The record handed to the rule belongs to somebody else.
   *
   * Impossible over the wire, because the server reads the record by the caller's
   * own id and never accepts one from the client. It is here so that fact is
   * checked rather than assumed: if it ever answers, the read returned the wrong
   * document, which is a real bug and must surface rather than quietly writing a
   * flag onto a stranger's record.
   */
  NotYourReferral = 'not-your-referral',
}

/** What a completed workout means for the caller's own referral record. */
export type ReferralTrainingDecision =
  /** Write this record back. It is the caller's own, with the flag now set. */
  | { kind: 'record'; record: ReferralDoc }
  /** The flag is already set. Nothing to write, and the caller may stop asking. */
  | { kind: 'already-recorded' }
  | { kind: 'refuse'; refusal: ReferralTrainingRefusal };

/** Everything `decideReferralTrainingReport` needs. Gathered by the caller; never fetched here. */
export interface ReferralTrainingFacts {
  /** The signed-in user reporting that they have trained. */
  callerUid: string;
  /** That caller's own `referral/<uid>` document, or null when they have none. */
  referral: ReferralDoc | null;
  /** Now, supplied by the caller so this function has no clock of its own. */
  now_utc: string;
}

/**
 * Decides whether to set the training flag on the caller's own referral record.
 *
 * The rule is deliberately narrow: absent to set, only on the caller's own record,
 * and it stores nothing but the flag and the moment it was set. The inviter learns
 * one bit — "they have started" — and can never learn how many workouts, how often,
 * or which. The server never reads anybody's training data to work this out; the
 * friend's own app reports it, once.
 *
 * It is NOT gated on the feature flag, for the same reason the qualification rule
 * is not: this is the only moment the fact is offered, and a flag that happens to
 * be off would lose it until the person's next workout — or for ever, because the
 * client stops asking once it has been recorded. Only the dashboard that displays
 * it and the notifications are gated.
 */
export function decideReferralTrainingReport(facts: ReferralTrainingFacts): ReferralTrainingDecision {
  const { callerUid, referral, now_utc } = facts;

  if (!referral) return { kind: 'refuse', refusal: ReferralTrainingRefusal.NoReferral };

  // a referral record's id IS the friend's user id, so this compares the record's
  // owner with the caller without needing a separate field to trust
  if (referral._id !== referralDocId(callerUid)) {
    return { kind: 'refuse', refusal: ReferralTrainingRefusal.NotYourReferral };
  }

  // absent to set, and only that. a record that already carries the moment keeps
  // the ORIGINAL moment: it is the first workout that means "they have started",
  // and overwriting it would quietly turn the field into "most recent workout",
  // which is a great deal more than this feature is allowed to share.
  if (referral.trainedAt_utc) return { kind: 'already-recorded' };

  return { kind: 'record', record: { ...referral, trainedAt_utc: now_utc } };
}

/**
 * What the inviter is shown against one friend. Highest first.
 *
 * There are exactly three, and there is deliberately no fourth. No counts, no
 * dates, no streaks, no last-seen. Adding one is a fresh product decision and a
 * fresh conversation about somebody else's privacy, not an extension of this one.
 */
export enum ReferralFriendStatus {
  /** The friend created an account through the invite. */
  Joined = 'joined',
  /** The friend has logged at least one workout. */
  Training = 'training',
  /** The friend has paid at least once. */
  Member = 'member',
}

/**
 * The one status shown for a friend, from their referral record.
 *
 * Precedence is Member, then Training, then Joined — a paying friend who has also
 * trained shows as Member, because that is the further-along fact.
 *
 * **The status only ever rises**, and there is nothing here that lowers it. A
 * friend who pays and then refunds a week later stays Member, because `qualified`
 * means "has paid at least once", not "is paying now". Lowering it would tell the
 * inviter something about somebody else's refund, which is further into that
 * person's business than this feature should ever go.
 */
export function decideReferralFriendStatus(
  referral: Pick<ReferralDoc, 'status' | 'trainedAt_utc' | 'disclosedAt_utc'>,
): ReferralFriendStatus {
  if (referral.status === ReferralStatus.Qualified) return ReferralFriendStatus.Member;
  // Training is the only one of the three about what somebody DOES inside the app,
  // so it is the only one withheld from a friend who was never told they would be
  // seen. This withholds the DISPLAY, not the fact — `trainedAt_utc` is still
  // recorded. See design-docs/referral-program.md, "The friends who joined before
  // phase 3".
  if (referral.trainedAt_utc && referral.disclosedAt_utc) return ReferralFriendStatus.Training;
  return ReferralFriendStatus.Joined;
}

/**
 * How many friends have to join before the inviter is congratulated. See
 * `decideThirdFriendMilestone`.
 */
export const REFERRAL_MILESTONE_FRIEND_COUNT = 3;

/** What a newly created referral means for the inviter's third-friend milestone. */
export enum ReferralMilestoneAction {
  /** Not there yet, or already sent. Nothing to do. */
  Nothing = 'nothing',
  /** Try to send it — after winning the one-shot claim, never before. */
  Send = 'send',
}

/**
 * Decide whether this inviter has just reached their third friend.
 *
 * The rule is `exactly at or past three, and not already recorded`, and the
 * "already recorded" half is why it is not simply `count === 3`. Phase 1's
 * deletion handling REMOVES a friend's referral record when they delete their
 * account, so an inviter's count can fall from 3 back to 2 and rise to 3 again —
 * with `=== 3` that congratulates them twice, and with `>= 3` and no marker it
 * congratulates them on every friend after the third, for ever.
 *
 * **The marker is not the guard against a race, only against a repeat.** Two
 * friends claiming in the same instant can both read a record with no marker and
 * both be told to send. The caller must win a one-shot claim before sending, and
 * `referralMilestoneClaimKey` is the key for it. Nothing here can express that,
 * because it is pure and the race is in the database.
 */
export function decideThirdFriendMilestone(
  referrer: Pick<ReferrerDoc, 'thirdFriendNotifiedAt_utc'> | null | undefined,
  friendCount: number,
): ReferralMilestoneAction {
  if (!referrer) return ReferralMilestoneAction.Nothing;
  if (referrer.thirdFriendNotifiedAt_utc) return ReferralMilestoneAction.Nothing;
  return friendCount >= REFERRAL_MILESTONE_FRIEND_COUNT
    ? ReferralMilestoneAction.Send
    : ReferralMilestoneAction.Nothing;
}

/**
 * The claim key that makes "congratulate this inviter once" true for ever.
 *
 * Keyed on the INVITER, unlike `referralQualifiedClaimKey` which is keyed on the
 * friend — this milestone is about the inviter reaching a number, and there is
 * exactly one of those per person for the rest of time.
 */
export function referralMilestoneClaimKey(inviterUid: string): string {
  return `referral_milestone_notify:${inviterUid}`;
}
